/**
 * Analytics Facade
 *
 * The single import that all feature code uses for analytics.
 * Registers provider adapters (GA4, PostHog, ServerRelay, Log)
 * and fans out every call to all active providers.
 *
 * Usage:
 *   import { analytics } from '../services/analytics/analytics';
 *   analytics.track('signup_completed', { method: 'google', invited: false });
 *   analytics.identify(userId, { plan: 'pro', email });
 *   analytics.page('/app/spaces');
 *   analytics.group(orgId, { plan: 'pro', seat_count: 5 });
 *
 * Provider swap: to add/remove a vendor change the providers registered in
 * App.tsx. Feature code never needs to change.
 *
 * Session model:
 *   - Exactly ONE session ID per browser tab, persisted in sessionStorage.
 *   - On module init, if sessionStorage already has a recent session (< 30 min
 *     idle), that ID is adopted. Otherwise a new one is generated.
 *   - reset() (called on logout) generates a new session ID and writes it to
 *     both the private field AND sessionStorage so the X-Session-ID header
 *     always matches event payloads.
 *   - session_ended fires on pagehide (via sendBeacon) and after 30 min idle.
 *
 * Anonymous ID:
 *   - An anonymous_id is assigned on first visit and persisted in localStorage
 *     so it survives page reloads and tab closure.
 *   - It is attached to every event and identify call.
 *   - On identify(), providers alias anonymous_id → user_id so pre-auth events
 *     can be stitched to the authenticated user in PostHog.
 */

import { createFileLogger } from '../../utils/logger';
import type { EventName, EventProps, UserTraits, OrgTraits, PageProps } from './events';
import { VALID_EVENTS } from './events';
import { API_BASE_URL } from '../../api/config';

const log = createFileLogger('ANALYTICS');

// Storage keys — all in one place so logoutUtils can import them
export const ANALYTICS_SESSION_KEY = 'analytics_session_id';
export const ANALYTICS_SESSION_ACTIVITY_KEY = 'analytics_session_last_activity';
export const ANALYTICS_ANONYMOUS_ID_KEY = 'analytics_anonymous_id';

/** Session idle threshold in ms before a new session starts. */
const SESSION_IDLE_THRESHOLD_MS = 30 * 60 * 1000; // 30 minutes
/** Idle timeout before emitting session_ended with reason 'idle'. */
const SESSION_IDLE_END_MS = 30 * 60 * 1000;

// ---------------------------------------------------------------------------
// Provider interface
// ---------------------------------------------------------------------------
export interface AnalyticsContext {
  userId?: string;
  orgId?: string;
  sessionId?: string;
  plan?: string;
  appVersion?: string;
}

export interface AnalyticsProvider {
  readonly name: string;
  init(ctx: AnalyticsContext): Promise<void>;
  track(event: string, props: Record<string, unknown>): void;
  identify(userId: string, traits?: Record<string, unknown>): void;
  alias?(anonymousId: string, userId: string): void;
  page(path: string, props?: Record<string, unknown>): void;
  group?(orgId: string, traits?: Record<string, unknown>): void;
  reset(): void;
  flush(): Promise<void>;
}

// ---------------------------------------------------------------------------
// Ambient context (enriched on every event automatically)
// ---------------------------------------------------------------------------
interface AmbientContext {
  userId?: string;
  orgId?: string;
  sessionId?: string;
  plan?: string;
  appVersion?: string;
  funnel_id?: string;
  persona?: string;
  anonymous_id?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
}

// ---------------------------------------------------------------------------
// Debug mode helpers
// ---------------------------------------------------------------------------
const DEBUG_KEY = 'analytics_debug';

function isDebugMode(): boolean {
  if (typeof window === 'undefined') return false;
  if (window.location.search.includes('debug_analytics=1')) {
    sessionStorage.setItem(DEBUG_KEY, '1');
    return true;
  }
  return sessionStorage.getItem(DEBUG_KEY) === '1';
}

/**
 * True when events should be sent to remote vendors (PostHog, GA, server relay).
 * False on localhost and common private LAN hostnames unless `?debug_analytics=1` is active.
 */
export function isTrackingEnabled(): boolean {
  if (isDebugMode()) return true;
  if (typeof window === 'undefined') return false;
  const hostname = window.location.hostname.toLowerCase();
  const isLocal =
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname === '::1' ||
    hostname.endsWith('.local') ||
    hostname.startsWith('192.168.') ||
    hostname.startsWith('10.');
  return !isLocal;
}

function debugLog(method: string, ...args: unknown[]) {
  if (isDebugMode()) {
    console.groupCollapsed(`[Analytics:${method}]`, args[0]);
    console.log(...args);
    console.groupEnd();
  }
}

// ---------------------------------------------------------------------------
// UUID helper
// ---------------------------------------------------------------------------
function generateId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

// ---------------------------------------------------------------------------
// Session helpers
// ---------------------------------------------------------------------------
/**
 * Read the current session ID from sessionStorage (canonical source of truth).
 * Returns undefined when called in non-browser environments.
 */
function readSessionId(): string | undefined {
  if (typeof sessionStorage === 'undefined') return undefined;
  return sessionStorage.getItem(ANALYTICS_SESSION_KEY) || undefined;
}

/**
 * Write a session ID to sessionStorage and return it.
 */
function writeSessionId(id: string): string {
  if (typeof sessionStorage !== 'undefined') {
    sessionStorage.setItem(ANALYTICS_SESSION_KEY, id);
  }
  return id;
}

/**
 * Touch the session activity timestamp. Called on every track().
 */
function touchSessionActivity(): void {
  if (typeof sessionStorage !== 'undefined') {
    sessionStorage.setItem(ANALYTICS_SESSION_ACTIVITY_KEY, Date.now().toString());
  }
}

/**
 * Returns the ms since the last tracked event, or Infinity if never set.
 */
function msSinceLastActivity(): number {
  if (typeof sessionStorage === 'undefined') return Infinity;
  const raw = sessionStorage.getItem(ANALYTICS_SESSION_ACTIVITY_KEY);
  if (!raw) return Infinity;
  return Date.now() - parseInt(raw, 10);
}

/**
 * Resolve the session ID to use at startup:
 * - If sessionStorage has an id and the last activity was < SESSION_IDLE_THRESHOLD_MS ago, adopt it.
 * - Otherwise generate a new one and persist it.
 */
function resolveStartupSessionId(): string {
  const existing = readSessionId();
  if (existing && msSinceLastActivity() < SESSION_IDLE_THRESHOLD_MS) {
    return existing;
  }
  return writeSessionId(generateId());
}

// ---------------------------------------------------------------------------
// Anonymous ID helpers
// ---------------------------------------------------------------------------
function resolveAnonymousId(): string {
  if (typeof localStorage === 'undefined') return generateId();
  const existing = localStorage.getItem(ANALYTICS_ANONYMOUS_ID_KEY);
  if (existing) return existing;
  const id = generateId();
  localStorage.setItem(ANALYTICS_ANONYMOUS_ID_KEY, id);
  return id;
}

// ---------------------------------------------------------------------------
// Analytics singleton
// ---------------------------------------------------------------------------
export class AnalyticsFacade {
  private providers: AnalyticsProvider[] = [];
  private ambient: AmbientContext = {};
  private _sessionId: string;
  private anonymousId: string;
  private sessionStartMs: number;
  private eventsInSession = 0;
  private agentRunsInSession = 0;
  private messagesSentInSession = 0;
  private idleTimer: ReturnType<typeof setTimeout> | null = null;
  private _aliasedUsers = new Set<string>();

  constructor() {
    this._sessionId = resolveStartupSessionId();
    this.anonymousId = resolveAnonymousId();
    this.sessionStartMs = Date.now();
    this.ambient.sessionId = this._sessionId;
    this.ambient.anonymous_id = this.anonymousId;
  }

  register(provider: AnalyticsProvider): void {
    this.providers.push(provider);
  }

  /** Call once at app startup after config is loaded. */
  async init(ctx: AnalyticsContext): Promise<void> {
    this.ambient = {
      ...ctx,
      sessionId: this._sessionId,
      anonymous_id: this.anonymousId,
    };
    for (const p of this.providers) {
      try {
        await p.init({ ...ctx, sessionId: this._sessionId });
      } catch (err) {
        log.warn(`[${p.name}] init failed:`, err);
      }
    }
    this._attachLifecycleListeners();
  }

  /** Update ambient context (e.g. after profile loads). */
  setContext(ctx: Partial<AmbientContext>): void {
    this.ambient = { ...this.ambient, ...ctx };
  }

  track<E extends EventName>(event: E, props: EventProps<E>): void {
    if (!isTrackingEnabled()) {
      debugLog('track', event, props);
      return;
    }

    if (!VALID_EVENTS.has(event)) {
      log.warn('Unknown event name:', event);
    }

    // Update session counters
    this.eventsInSession++;
    if (event === 'first_agent_run_completed' || event === 'second_agent_run_completed' || event === 'third_agent_run_completed') {
      this.agentRunsInSession++;
    }
    if (event === 'chat_message_sent' || event === 'first_user_message') {
      this.messagesSentInSession++;
    }
    touchSessionActivity();
    this._resetIdleTimer();

    const enriched: Record<string, unknown> = {
      ...props,
      correlation_id: generateId(),
      session_id: this.ambient.sessionId,
      anonymous_id: this.ambient.anonymous_id,
      ...(this.ambient.userId && { $user_id: this.ambient.userId }),
      ...(this.ambient.orgId && { org_id: this.ambient.orgId }),
      ...(this.ambient.plan && { plan: this.ambient.plan }),
      ...(this.ambient.funnel_id && { funnel_id: this.ambient.funnel_id }),
      ...(this.ambient.persona && { persona: this.ambient.persona }),
      ...(this.ambient.utm_source && { utm_source: this.ambient.utm_source }),
      ...(this.ambient.utm_medium && { utm_medium: this.ambient.utm_medium }),
      ...(this.ambient.utm_campaign && { utm_campaign: this.ambient.utm_campaign }),
    };

    debugLog('track', event, enriched);

    for (const p of this.providers) {
      try {
        p.track(event, enriched);
      } catch (err) {
        log.warn(`[${p.name}] track(${event}) failed:`, err);
      }
    }
  }

  identify(userId: string, traits?: UserTraits): void {
    if (!isTrackingEnabled()) {
      debugLog('identify', userId, traits);
      return;
    }

    const wasAnonymous = !this.ambient.userId;
    this.ambient.userId = userId;
    if (traits?.plan) this.ambient.plan = traits.plan;
    if (traits?.org_id) this.ambient.orgId = traits.org_id;
    if (traits?.funnel_id) this.ambient.funnel_id = traits.funnel_id;
    if (traits?.persona) this.ambient.persona = traits.persona;

    const mergedTraits = {
      ...(traits as Record<string, unknown> | undefined),
      anonymous_id: this.anonymousId,
    };

    debugLog('identify', userId, mergedTraits);

    for (const p of this.providers) {
      try {
        p.identify(userId, mergedTraits);
      } catch (err) {
        log.warn(`[${p.name}] identify(${userId}) failed:`, err);
      }
    }

    // One-time alias of anonymous_id → user_id so pre-auth events stitch together
    if (wasAnonymous && !this._aliasedUsers.has(userId)) {
      this._aliasedUsers.add(userId);
      for (const p of this.providers) {
        try {
          p.alias?.(this.anonymousId, userId);
        } catch (err) {
          log.warn(`[${p.name}] alias failed:`, err);
        }
      }
    }
  }

  page(path: string, props?: PageProps): void {
    if (!isTrackingEnabled()) {
      debugLog('page', path, props);
      return;
    }

    debugLog('page', path, props);

    const enriched: Record<string, unknown> = {
      ...props,
      session_id: this.ambient.sessionId,
      anonymous_id: this.ambient.anonymous_id,
    };

    for (const p of this.providers) {
      try {
        p.page(path, enriched);
      } catch (err) {
        log.warn(`[${p.name}] page(${path}) failed:`, err);
      }
    }
  }

  group(orgId: string, traits?: OrgTraits): void {
    if (!isTrackingEnabled()) {
      debugLog('group', orgId, traits);
      return;
    }

    this.ambient.orgId = orgId;
    debugLog('group', orgId, traits);

    for (const p of this.providers) {
      try {
        p.group?.(orgId, traits as Record<string, unknown> | undefined);
      } catch (err) {
        log.warn(`[${p.name}] group(${orgId}) failed:`, err);
      }
    }
  }

  reset(): void {
    // Fire session_ended synchronously before reset
    this._emitSessionEnded('logout');

    // Generate a fresh session ID and write it to both the private field AND
    // sessionStorage so the X-Session-ID header always stays in sync with
    // event payloads after logout → login.
    const newId = generateId();
    this._sessionId = newId;
    writeSessionId(newId);
    touchSessionActivity();
    this.sessionStartMs = Date.now();
    this.eventsInSession = 0;
    this.agentRunsInSession = 0;
    this.messagesSentInSession = 0;

    this.ambient = {
      sessionId: newId,
      anonymous_id: this.anonymousId,
    };

    if (this.idleTimer) {
      clearTimeout(this.idleTimer);
      this.idleTimer = null;
    }

    for (const p of this.providers) {
      try {
        p.reset();
      } catch (err) {
        log.warn(`[${p.name}] reset failed:`, err);
      }
    }
  }

  async flush(): Promise<void> {
    await Promise.all(
      this.providers.map((p) =>
        p.flush().catch((err) => log.warn(`[${p.name}] flush failed:`, err))
      )
    );
  }

  getSessionId(): string {
    // Always canonical — read from sessionStorage if available
    return readSessionId() || this._sessionId;
  }

  getAnonymousId(): string {
    return this.anonymousId;
  }

  // ---------------------------------------------------------------------------
  // Session lifecycle (private)
  // ---------------------------------------------------------------------------
  private _attachLifecycleListeners(): void {
    if (typeof window === 'undefined') return;

    // Emit session_ended via sendBeacon on actual page unload
    window.addEventListener('pagehide', (event) => {
      if (!event.persisted) {
        this._emitSessionEndedBeacon('pagehide');
      }
    });

    // Track visibility changes for idle timeout
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        this._startIdleTimer();
      } else {
        this._resetIdleTimer();
      }
    });
  }

  private _resetIdleTimer(): void {
    if (this.idleTimer) {
      clearTimeout(this.idleTimer);
      this.idleTimer = null;
    }
    // Only restart timer when tab is hidden
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
      this._startIdleTimer();
    }
  }

  private _startIdleTimer(): void {
    if (this.idleTimer) return;
    this.idleTimer = setTimeout(() => {
      this.idleTimer = null;
      this._emitSessionEnded('idle');
      // Rotate to a new session ID after idle expiry
      const newId = generateId();
      this._sessionId = newId;
      writeSessionId(newId);
      this.ambient.sessionId = newId;
      this.sessionStartMs = Date.now();
      this.eventsInSession = 0;
      this.agentRunsInSession = 0;
      this.messagesSentInSession = 0;
    }, SESSION_IDLE_END_MS);
  }

  private _emitSessionEnded(reason: 'pagehide' | 'idle' | 'logout' | 'reset'): void {
    if (!isTrackingEnabled()) return;
    if (this.eventsInSession === 0) return; // nothing to record

    const props = {
      session_id: this.getSessionId(),
      duration_ms: Date.now() - this.sessionStartMs,
      end_reason: reason,
      events_in_session: this.eventsInSession,
      agent_runs_in_session: this.agentRunsInSession,
      messages_sent_in_session: this.messagesSentInSession,
      correlation_id: generateId(),
      anonymous_id: this.anonymousId,
      ...(this.ambient.userId && { $user_id: this.ambient.userId }),
      ...(this.ambient.orgId && { org_id: this.ambient.orgId }),
    };

    for (const p of this.providers) {
      try {
        p.track('session_ended', props);
      } catch {
        // Best-effort; don't let errors in session_ended affect anything
      }
    }
  }

  private _emitSessionEndedBeacon(reason: 'pagehide' | 'idle'): void {
    if (!isTrackingEnabled()) return;
    if (this.eventsInSession === 0) return;

    const payload = {
      events: [{
        event: 'session_ended',
        props: {
          session_id: this.getSessionId(),
          duration_ms: Date.now() - this.sessionStartMs,
          end_reason: reason,
          events_in_session: this.eventsInSession,
          agent_runs_in_session: this.agentRunsInSession,
          messages_sent_in_session: this.messagesSentInSession,
          correlation_id: generateId(),
          anonymous_id: this.anonymousId,
          ...(this.ambient.userId && { $user_id: this.ambient.userId }),
          ...(this.ambient.orgId && { org_id: this.ambient.orgId }),
        },
        ts: new Date().toISOString(),
      }],
    };

    // sendBeacon can't attach Authorization headers, so route through the
    // unauthenticated /analytics/beacon endpoint with user_id in the body.
    // The endpoint only accepts allowlisted VALID_EVENTS, so the trust impact
    // is bounded.
    const beaconBody: Record<string, unknown> = {
      ...payload,
      user_id: this.ambient.userId,
      anonymous_id: this.anonymousId,
      org_id: this.ambient.orgId,
    };

    if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
      const blob = new Blob([JSON.stringify(beaconBody)], { type: 'application/json' });
      navigator.sendBeacon(`${API_BASE_URL}/analytics/beacon`, blob);
    } else {
      fetch(`${API_BASE_URL}/analytics/beacon`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(beaconBody),
        keepalive: true,
      }).catch(() => { /* best effort */ });
    }
  }
}

export const analytics = new AnalyticsFacade();
export default analytics;

// Ensure sessionStorage is always in sync with the facade's session on startup.
// The facade constructor already calls resolveStartupSessionId() which writes to
// sessionStorage, so this is just a guard for SSR / test environments where
// sessionStorage may not have been written yet.
if (typeof sessionStorage !== 'undefined') {
  const existing = sessionStorage.getItem(ANALYTICS_SESSION_KEY);
  if (!existing) {
    sessionStorage.setItem(ANALYTICS_SESSION_KEY, analytics.getSessionId());
  }
}
